// 分类数据管理实现

import type {
  Category,
  BookInfo,
  SearchQuery,
  SearchResult
} from '@/types/reading';

import type { StorageManager } from './storage';
import { BookSearchManager } from './bookSearchManager';
import { BookLibrary } from './bookLibrary';

export class CategoryManager {
  private storage: StorageManager;
  private searchManager: BookSearchManager;
  private bookLibrary: BookLibrary;
  private categoryCache: Map<string, Category[]> = new Map();
  private readonly CACHE_TTL = 60 * 60 * 1000; // 1小时缓存

  constructor(
    storage: StorageManager,
    searchManager: BookSearchManager,
    bookLibrary: BookLibrary
  ) {
    this.storage = storage;
    this.searchManager = searchManager;
    this.bookLibrary = bookLibrary;
  }

  // ============================================================================
  // 分类信息提取和组织
  // ============================================================================

  async extractCategoriesFromBooks(): Promise<Category[]> {
    try {
      // 从图书库中提取分类信息
      const libraryBooks = await this.bookLibrary.getLibraryBooks();
      const categoryMap = new Map<string, {
        books: BookInfo[];
        coverImages: Set<string>;
      }>();

      // 统计每个分类的图书
      libraryBooks.forEach(book => {
        const categoryName = book.category || 'Unknown';
        
        if (!categoryMap.has(categoryName)) {
          categoryMap.set(categoryName, {
            books: [],
            coverImages: new Set()
          });
        }

        const categoryData = categoryMap.get(categoryName)!;
        categoryData.books.push(book);
        
        // 收集封面图片
        if (book.coverUrl) {
          categoryData.coverImages.add(book.coverUrl);
        }
      });

      // 转换为Category对象
      const categories: Category[] = [];
      let sortOrder = 0;

      for (const [categoryName, data] of categoryMap.entries()) {
        const category: Category = {
          id: this.generateCategoryId(categoryName),
          name: categoryName,
          bookCount: data.books.length,
          coverImages: Array.from(data.coverImages).slice(0, 4), // 最多4张封面
          description: this.generateCategoryDescription(categoryName, data.books.length),
          sortOrder: sortOrder++
        };

        categories.push(category);
      }

      // 按图书数量排序
      categories.sort((a, b) => b.bookCount - a.bookCount);

      return categories;

    } catch (error) {
      console.error('Extract categories from books failed:', error);
      return [];
    }
  }

  async extractCategoriesFromSources(): Promise<Category[]> {
    try {
      // 从书源中提取分类信息（如果书源支持分类浏览）
      const categories: Category[] = [];
      
      // 这里可以扩展为从书源的分类页面提取分类信息
      // 目前返回空数组，后续可以根据具体书源格式实现
      
      return categories;

    } catch (error) {
      console.error('Extract categories from sources failed:', error);
      return [];
    }
  }

  async getAllCategories(): Promise<Category[]> {
    try {
      const cacheKey = 'all_categories';
      const cached = this.categoryCache.get(cacheKey);
      
      if (cached) {
        return cached;
      }

      // 合并从图书库和书源提取的分类
      const [libraryCategories, sourceCategories] = await Promise.all([
        this.extractCategoriesFromBooks(),
        this.extractCategoriesFromSources()
      ]);

      const allCategories = this.mergeCategories(libraryCategories, sourceCategories);
      
      // 缓存结果
      this.categoryCache.set(cacheKey, allCategories);
      
      // 设置缓存过期
      setTimeout(() => {
        this.categoryCache.delete(cacheKey);
      }, this.CACHE_TTL);

      return allCategories;

    } catch (error) {
      console.error('Get all categories failed:', error);
      return [];
    }
  }

  private mergeCategories(libraryCategories: Category[], sourceCategories: Category[]): Category[] {
    const categoryMap = new Map<string, Category>();

    // 添加图书库分类
    libraryCategories.forEach(category => {
      categoryMap.set(category.name.toLowerCase(), category);
    });

    // 合并书源分类
    sourceCategories.forEach(sourceCategory => {
      const key = sourceCategory.name.toLowerCase();
      const existing = categoryMap.get(key);

      if (existing) {
        // 合并现有分类
        existing.bookCount += sourceCategory.bookCount;
        existing.coverImages = [
          ...existing.coverImages,
          ...sourceCategory.coverImages
        ].slice(0, 4);
      } else {
        categoryMap.set(key, sourceCategory);
      }
    });

    return Array.from(categoryMap.values()).sort((a, b) => b.bookCount - a.bookCount);
  }

  // ============================================================================
  // 分类图书列表获取
  // ============================================================================

  async getCategoryBooks(categoryId: string, page: number = 1, pageSize: number = 20): Promise<SearchResult> {
    try {
      const category = await this.getCategoryById(categoryId);
      if (!category) {
        return this.createEmptyResult(page, pageSize);
      }

      // 从图书库获取该分类的图书
      const libraryBooks = await this.bookLibrary.getLibraryBooks();
      const categoryBooks = libraryBooks.filter(book => 
        book.category.toLowerCase() === category.name.toLowerCase()
      );

      // 如果图书库中的图书不够，从书源搜索更多
      if (categoryBooks.length < pageSize) {
        const additionalBooks = await this.searchCategoryFromSources(category.name, pageSize - categoryBooks.length);
        categoryBooks.push(...additionalBooks);
      }

      // 去重
      const uniqueBooks = this.deduplicateBooks(categoryBooks);

      // 分页
      const startIndex = (page - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      const paginatedBooks = uniqueBooks.slice(startIndex, endIndex);

      return {
        books: paginatedBooks,
        total: uniqueBooks.length,
        page,
        pageSize,
        suggestions: [],
        searchTime: Date.now()
      };

    } catch (error) {
      console.error('Get category books failed:', error);
      return this.createEmptyResult(page, pageSize);
    }
  }

  private async searchCategoryFromSources(categoryName: string, limit: number): Promise<BookInfo[]> {
    try {
      // 使用分类名作为关键词搜索
      const searchQuery: SearchQuery = {
        keyword: '',
        category: categoryName,
        page: 1,
        pageSize: limit,
        sortBy: 'popularity'
      };

      const result = await this.searchManager.searchBooksAcrossSources(searchQuery);
      return result.books;

    } catch (error) {
      console.error('Search category from sources failed:', error);
      return [];
    }
  }

  async getCategoryById(categoryId: string): Promise<Category | null> {
    try {
      const allCategories = await this.getAllCategories();
      return allCategories.find(category => category.id === categoryId) || null;
    } catch (error) {
      console.error('Get category by id failed:', error);
      return null;
    }
  }

  async getCategoryByName(categoryName: string): Promise<Category | null> {
    try {
      const allCategories = await this.getAllCategories();
      return allCategories.find(category => 
        category.name.toLowerCase() === categoryName.toLowerCase()
      ) || null;
    } catch (error) {
      console.error('Get category by name failed:', error);
      return null;
    }
  }

  // ============================================================================
  // 分类收藏和排序功能
  // ============================================================================

  async getFavoriteCategories(): Promise<Category[]> {
    try {
      const favoriteNames = await this.storage.getUserData('favorite_categories') || [];
      const allCategories = await this.getAllCategories();
      
      return allCategories.filter(category => 
        favoriteNames.includes(category.name)
      );
    } catch (error) {
      console.error('Get favorite categories failed:', error);
      return [];
    }
  }

  async addFavoriteCategory(categoryName: string): Promise<boolean> {
    try {
      const favoriteNames = await this.storage.getUserData('favorite_categories') || [];
      
      if (!favoriteNames.includes(categoryName)) {
        favoriteNames.push(categoryName);
        await this.storage.saveUserData('favorite_categories', favoriteNames);
      }

      return true;
    } catch (error) {
      console.error('Add favorite category failed:', error);
      return false;
    }
  }

  async removeFavoriteCategory(categoryName: string): Promise<boolean> {
    try {
      const favoriteNames = await this.storage.getUserData('favorite_categories') || [];
      const updatedNames = favoriteNames.filter((name: string) => name !== categoryName);
      
      await this.storage.saveUserData('favorite_categories', updatedNames);
      return true;
    } catch (error) {
      console.error('Remove favorite category failed:', error);
      return false;
    }
  }

  async getCustomCategoryOrder(): Promise<string[]> {
    try {
      return await this.storage.getUserData('category_order') || [];
    } catch (error) {
      console.error('Get custom category order failed:', error);
      return [];
    }
  }

  async setCustomCategoryOrder(categoryNames: string[]): Promise<boolean> {
    try {
      await this.storage.saveUserData('category_order', categoryNames);
      
      // 清除缓存以应用新排序
      this.categoryCache.clear();
      
      return true;
    } catch (error) {
      console.error('Set custom category order failed:', error);
      return false;
    }
  }

  async getSortedCategories(): Promise<Category[]> {
    try {
      const allCategories = await this.getAllCategories();
      const customOrder = await this.getCustomCategoryOrder();

      if (customOrder.length === 0) {
        return allCategories;
      }

      // 按自定义顺序排序
      const sortedCategories: Category[] = [];
      const remainingCategories = [...allCategories];

      // 先添加自定义顺序中的分类
      customOrder.forEach(categoryName => {
        const index = remainingCategories.findIndex(cat => 
          cat.name.toLowerCase() === categoryName.toLowerCase()
        );
        
        if (index >= 0) {
          sortedCategories.push(remainingCategories.splice(index, 1)[0]);
        }
      });

      // 添加剩余的分类
      sortedCategories.push(...remainingCategories);

      return sortedCategories;
    } catch (error) {
      console.error('Get sorted categories failed:', error);
      return await this.getAllCategories();
    }
  }

  // ============================================================================
  // 分类统计和分析
  // ============================================================================

  async getCategoryStats(): Promise<{
    totalCategories: number;
    favoriteCategories: number;
    averageBooksPerCategory: number;
    topCategories: Array<{ name: string; bookCount: number }>;
    recentlyAddedCategories: Category[];
  }> {
    try {
      const [allCategories, favoriteCategories] = await Promise.all([
        this.getAllCategories(),
        this.getFavoriteCategories()
      ]);

      const totalBooks = allCategories.reduce((sum, cat) => sum + cat.bookCount, 0);
      const averageBooksPerCategory = allCategories.length > 0 ? totalBooks / allCategories.length : 0;

      const topCategories = allCategories
        .slice(0, 10)
        .map(cat => ({ name: cat.name, bookCount: cat.bookCount }));

      // 最近添加的分类（基于sortOrder，较新的sortOrder较大）
      const recentlyAddedCategories = allCategories
        .sort((a, b) => (b.sortOrder || 0) - (a.sortOrder || 0))
        .slice(0, 5);

      return {
        totalCategories: allCategories.length,
        favoriteCategories: favoriteCategories.length,
        averageBooksPerCategory: Math.round(averageBooksPerCategory * 100) / 100,
        topCategories,
        recentlyAddedCategories
      };

    } catch (error) {
      console.error('Get category stats failed:', error);
      return {
        totalCategories: 0,
        favoriteCategories: 0,
        averageBooksPerCategory: 0,
        topCategories: [],
        recentlyAddedCategories: []
      };
    }
  }

  // ============================================================================
  // 工具方法
  // ============================================================================

  private generateCategoryId(categoryName: string): string {
    return `category_${categoryName.toLowerCase().replace(/[^a-z0-9]/g, '_')}_${Date.now()}`;
  }

  private generateCategoryDescription(categoryName: string, bookCount: number): string {
    const descriptions: Record<string, string> = {
      '玄幻': '包含修仙、魔法、异世界等奇幻元素的小说',
      '都市': '以现代都市生活为背景的现实题材小说',
      '历史': '以历史时期为背景的小说作品',
      '科幻': '包含科学技术和未来世界的幻想小说',
      '武侠': '以武功、江湖为主题的传统武侠小说',
      '言情': '以爱情为主线的浪漫小说',
      '悬疑': '以推理、悬疑为主要元素的小说',
      '军事': '以军事题材为背景的小说作品'
    };

    const baseDescription = descriptions[categoryName] || `${categoryName}类型的小说作品`;
    return `${baseDescription}，共收录 ${bookCount} 本图书`;
  }

  private deduplicateBooks(books: BookInfo[]): BookInfo[] {
    const seen = new Set<string>();
    return books.filter(book => {
      const key = `${book.title.toLowerCase()}_${book.author.toLowerCase()}`;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }

  private createEmptyResult(page: number, pageSize: number): SearchResult {
    return {
      books: [],
      total: 0,
      page,
      pageSize,
      suggestions: [],
      searchTime: Date.now()
    };
  }

  // ============================================================================
  // 缓存管理
  // ============================================================================

  clearCache(): void {
    this.categoryCache.clear();
  }

  async refreshCategories(): Promise<void> {
    this.clearCache();
    await this.getAllCategories();
  }

  getCacheStats(): { cacheSize: number } {
    return {
      cacheSize: this.categoryCache.size
    };
  }
}